import { useState, useRef, useEffect, useCallback } from 'react';
import {
  GTM_CLICK_SEARCH_BAR_ABANDONED,
  GTM_CLICK_SEARCH_BAR_FOCUSED,
  GTM_CLICK_SEARCH_BAR_TYPING,
  GTM_CLICK_SEARCH_BAR_SUBMITTED,
} from 'constants/events';
import { trackEvent } from 'utils/google-tag-manager';

interface UseSearchBarTrackingProps {
  textInputRef: React.RefObject<HTMLInputElement> | null;
  locationQuery: string;
  isHomePage: boolean;
  onSearchPage: boolean;
  isInPanel: boolean;
}

const getTrackingContext = (isHomePage: boolean, onSearchPage: boolean, isInPanel: boolean): 'home page' | 'area page' | 'map search' => {
  if (isHomePage) {
    return 'home page';
  } else if (onSearchPage || isInPanel) {
    return 'map search';
  } else {
    return 'area page';
  }
};

const getCharacterType = (char: string): 'numbers' | 'letters' | 'other' => {
  if (/[0-9]/.test(char)) {
    return 'numbers';
  } else if (/[a-zA-Z]/.test(char)) {
    return 'letters';
  } else {
    return 'other';
  }
};

export const useSearchBarTracking = ({
  textInputRef,
  locationQuery,
  isHomePage,
  onSearchPage,
  isInPanel,
}: UseSearchBarTrackingProps) => {
  const [submittedSearch, setSubmittedSearch] = useState(false);
  const submittedSearchRef = useRef(submittedSearch);
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const [potentialAbandonment, setPotentialAbandonment] = useState(false);
  const previousQueryRef = useRef<string>('');

  const trackingContext = getTrackingContext(isHomePage, onSearchPage, isInPanel);

  useEffect(() => {
    submittedSearchRef.current = submittedSearch;
  }, [submittedSearch]);

  useEffect(() => {
    previousQueryRef.current = locationQuery;
  }, [locationQuery]);

  const handleFocus = useCallback(() => {
    setSubmittedSearch(false);
    setHasStartedTyping(false);
    setPotentialAbandonment(false);
    const eventData = { event: 'UiAllSrcBClk', trackingContext };
    trackEvent(GTM_CLICK_SEARCH_BAR_FOCUSED);
  }, [trackingContext]);

  const handleInput = useCallback((newValue: string) => {
    const previousValue = previousQueryRef.current;
    let characterType: 'numbers' | 'letters' | 'other' | 'backspace' | 'paste_or_replace' = 'other';

    if (newValue.length > previousValue.length) {
      const lastChar = newValue.slice(-1);
      characterType = getCharacterType(lastChar);
    } else if (newValue.length < previousValue.length) {
      characterType = 'backspace';
    } else if (newValue !== previousValue) {
      characterType = 'paste_or_replace';
    }

    if (!hasStartedTyping && newValue.length > 0) {
      const eventData = { event: 'UiAllSrcBTyp', trackingContext, characterType };
      trackEvent(GTM_CLICK_SEARCH_BAR_TYPING);
      setHasStartedTyping(true);
    } else if (hasStartedTyping && newValue.length === 0) {
      setHasStartedTyping(false);
    }
    setPotentialAbandonment(false);
  }, [trackingContext, hasStartedTyping]);

  const handleBlur = useCallback(() => {
    setPotentialAbandonment(true);
    setHasStartedTyping(false);
  }, []);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    if (potentialAbandonment && !submittedSearch && locationQuery.trim() !== '') {
      timeoutId = setTimeout(() => {
        const eventData = { event: 'UiAllSrcBSrcAbnd' };
        trackEvent(GTM_CLICK_SEARCH_BAR_ABANDONED);
      }, 100);
    } else if (potentialAbandonment && (submittedSearch || locationQuery.trim() === '')) {
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [potentialAbandonment, submittedSearch, locationQuery, trackingContext]);

  const markSearchAsSubmitted = useCallback(() => {
    setSubmittedSearch(true);
    submittedSearchRef.current = true;
    setPotentialAbandonment(false);
    setHasStartedTyping(false);
    
    trackEvent(GTM_CLICK_SEARCH_BAR_SUBMITTED, 'All', locationQuery);
  }, [trackingContext, locationQuery]);

  return {
    handleFocus,
    handleInput,
    handleBlur,
    markSearchAsSubmitted,
  };
};
